import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Button, Statistic, Table, Tag, Modal, InputNumber,
  Radio, Typography, Divider, Space, Progress, Tooltip, Badge, message, Tabs, Empty
} from 'antd';
import {
  WalletOutlined, ReloadOutlined, ThunderboltOutlined, BarChartOutlined,
  RocketOutlined, SafetyOutlined, CheckCircleOutlined, InfoCircleOutlined,
  CrownOutlined, EyeOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://127.0.0.1:4100');

const getToken = () => localStorage.getItem('token');

const axiosInst = axios.create({ baseURL: API_BASE });
axiosInst.interceptors.request.use(cfg => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// 模型提供商图标
const PROVIDER_ICONS = {
  zhipu: '🧠', deepseek: '🔮', qwen: '🌟', openai: '🤖',
  anthropic: '🎭', google: '🌐', mirrorstages: '🪞'
};

// 充值套餐
const RECHARGE_PACKAGES = [
  { amount: 10,  label: '¥10',  bonus: '',         tag: null },
  { amount: 30,  label: '¥30',  bonus: '',         tag: null },
  { amount: 50,  label: '¥50',  bonus: '+¥2 赠送',  tag: '推荐' },
  { amount: 100, label: '¥100', bonus: '+¥8 赠送',  tag: '优惠' },
  { amount: 200, label: '¥200', bonus: '+¥20 赠送', tag: '超值' },
  { amount: 500, label: '¥500', bonus: '+¥60 赠送', tag: '最划算' },
];

export default function PlatformAI() {
  const [balance, setBalance] = useState({ balance: 0, total_recharged: 0, total_consumed: 0 });
  const [pricing, setPricing] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rechargeVisible, setRechargeVisible] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(50);
  const [customAmount, setCustomAmount] = useState(null);
  const [payMethod, setPayMethod] = useState('wechat');
  const [recharging, setRecharging] = useState(false);

  const fetchBalance = useCallback(async () => {
    try {
      const { data } = await axiosInst.get('/api/v2/platform-ai/balance');
      if (data.success) setBalance(data.data);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchPricing = useCallback(async () => {
    try {
      const { data } = await axiosInst.get('/api/v2/platform-ai/pricing');
      if (data.success) setPricing(data.data);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchTransactions = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const { data } = await axiosInst.get(`/api/v2/platform-ai/transactions?page=${page}&limit=15`);
      if (data.success) { setTransactions(data.data); setTxTotal(data.total); }
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchBalance();
    fetchPricing();
    fetchTransactions();
  }, [fetchBalance, fetchPricing, fetchTransactions]);

  const handleRecharge = async () => {
    const amt = customAmount || selectedPackage;
    if (!amt || amt <= 0) { message.warning('请选择或输入充值金额'); return; }
    setRecharging(true);
    try {
      const { data } = await axiosInst.post('/api/v2/platform-ai/recharge', { amount: amt, payment_method: payMethod });
      if (data.success) {
        message.success(`充值成功！余额 ¥${parseFloat(data.balance).toFixed(2)}`);
        setRechargeVisible(false);
        setCustomAmount(null);
        fetchBalance();
        fetchTransactions();
      } else {
        message.error(data.message || '充值失败');
      }
    } catch (e) {
      message.error('充值请求失败，请稍后重试');
    }
    setRecharging(false);
  };

  const txColumns = [
    {
      title: '时间',
      dataIndex: 'created_at',
      width: 160,
      render: v => new Date(v).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    },
    {
      title: '类型',
      dataIndex: 'type',
      width: 80,
      render: v => v === 'recharge'
        ? <Tag color="success">充值</Tag>
        : v === 'consume' ? <Tag color="processing">消耗</Tag>
        : <Tag color="warning">退款</Tag>
    },
    { title: '说明', dataIndex: 'description', ellipsis: true },
    {
      title: '模型',
      dataIndex: 'model_name',
      width: 140,
      render: v => v ? <Tag>{v}</Tag> : '-'
    },
    {
      title: 'Tokens',
      dataIndex: 'tokens_used',
      width: 90,
      render: v => v > 0 ? v.toLocaleString() : '-'
    },
    {
      title: '金额',
      dataIndex: 'amount',
      width: 90,
      render: (v, r) => (
        <Text style={{ color: r.type === 'recharge' ? '#52c41a' : '#ff4d4f', fontWeight: 600 }}>
          {r.type === 'recharge' ? '+' : '-'}¥{Math.abs(v).toFixed(4)}
        </Text>
      )
    },
    {
      title: '余额',
      dataIndex: 'balance_after',
      width: 90,
      render: v => v != null ? <Text type="secondary">¥{parseFloat(v).toFixed(2)}</Text> : '-'
    },
  ];

  const pricingColumns = [
    {
      title: '模型',
      dataIndex: 'model_name',
      render: (v, r) => (
        <Space>
          <span>{PROVIDER_ICONS[r.provider] || '🤖'}</span>
          <div>
            <div style={{ fontWeight: 600 }}>{r.display_name || v}</div>
            <Text type="secondary" style={{ fontSize: 11 }}>{v}</Text>
          </div>
        </Space>
      )
    },
    {
      title: '输入价格',
      dataIndex: 'price_input',
      width: 140,
      render: v => v === 0
        ? <Tag color="success">免费</Tag>
        : <Text>¥{parseFloat(v).toFixed(4)} / 1K tokens</Text>
    },
    {
      title: '输出价格',
      dataIndex: 'price_output',
      width: 140,
      render: v => v === 0
        ? <Tag color="success">免费</Tag>
        : <Text>¥{parseFloat(v).toFixed(4)} / 1K tokens</Text>
    },
    {
      title: '上下文',
      dataIndex: 'context_length',
      width: 90,
      render: v => `${(v / 1000).toFixed(0)}K`
    },
    {
      title: '能力',
      dataIndex: 'supports_vision',
      width: 80,
      render: v => v ? <Tag color="purple" icon={<EyeOutlined />}>视觉</Tag> : <Tag>文本</Tag>
    },
    {
      title: '说明',
      dataIndex: 'description',
      ellipsis: true,
      render: v => <Text type="secondary">{v}</Text>
    },
  ];

  const finalAmount = customAmount || selectedPackage;
  const bonusInfo = RECHARGE_PACKAGES.find(p => p.amount === finalAmount);

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <RocketOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          平台AI模型
        </Title>
        <Text type="secondary">使用平台统一提供的AI算力，无需自行配置 API Key，按量计费，灵活便捷</Text>
      </div>

      {/* 余额卡片区 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card
            style={{ background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)', border: 'none' }}
            bodyStyle={{ padding: '20px 24px' }}
          >
            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, marginBottom: 4 }}>
              <WalletOutlined /> 当前余额
            </div>
            <div style={{ fontSize: 36, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
              ¥{parseFloat(balance.balance || 0).toFixed(2)}
            </div>
            <Button
              type="primary"
              ghost
              icon={<ThunderboltOutlined />}
              style={{ marginTop: 12, borderColor: 'rgba(255,255,255,0.6)', color: '#fff' }}
              onClick={() => setRechargeVisible(true)}
            >
              立即充值
            </Button>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bodyStyle={{ padding: '20px 24px' }}>
            <Statistic
              title={<span><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />累计充值</span>}
              value={parseFloat(balance.total_recharged || 0).toFixed(2)}
              prefix="¥"
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card bodyStyle={{ padding: '20px 24px' }}>
            <Statistic
              title={<span><BarChartOutlined style={{ color: '#ff7a00', marginRight: 4 }} />累计消耗</span>}
              value={parseFloat(balance.total_consumed || 0).toFixed(4)}
              prefix="¥"
              valueStyle={{ color: '#ff7a00' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 平台优势说明 */}
      <Card style={{ marginBottom: 24, background: '#f6ffed', border: '1px solid #b7eb8f' }}>
        <Row gutter={32}>
          {[
            { icon: <SafetyOutlined style={{ color: '#52c41a' }} />, title: '无需配置', desc: '不用申请 API Key，开通即用' },
            { icon: <ThunderboltOutlined style={{ color: '#1890ff' }} />, title: '按量计费', desc: '用多少扣多少，余额随时可查' },
            { icon: <CrownOutlined style={{ color: '#faad14' }} />, title: '多模型可选', desc: '国内外主流模型一站接入' },
            { icon: <InfoCircleOutlined style={{ color: '#722ed1' }} />, title: '优先服务', desc: '平台统一优化，稳定低延迟' },
          ].map((item, i) => (
            <Col xs={12} sm={6} key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 24, marginBottom: 4 }}>{item.icon}</div>
              <div style={{ fontWeight: 600 }}>{item.title}</div>
              <Text type="secondary" style={{ fontSize: 12 }}>{item.desc}</Text>
            </Col>
          ))}
        </Row>
      </Card>

      <Tabs defaultActiveKey="pricing">
        <TabPane tab={<span><BarChartOutlined /> 模型定价</span>} key="pricing">
          <Card>
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
              以下价格为平台内部计费价格，按实际 token 用量扣除余额。1K tokens ≈ 约700个汉字。
            </Paragraph>
            <Table
              dataSource={pricing}
              columns={pricingColumns}
              rowKey="id"
              pagination={false}
              size="middle"
              locale={{ emptyText: <Empty description="暂无定价数据" /> }}
            />
          </Card>
        </TabPane>

        <TabPane tab={<span><ReloadOutlined /> 充值 / 消费记录</span>} key="transactions">
          <Card>
            <Table
              dataSource={transactions}
              columns={txColumns}
              rowKey="id"
              loading={loading}
              size="middle"
              pagination={{
                current: txPage,
                total: txTotal,
                pageSize: 15,
                onChange: p => { setTxPage(p); fetchTransactions(p); },
                showTotal: t => `共 ${t} 条记录`
              }}
              locale={{ emptyText: <Empty description="暂无记录，充值后开始使用吧！" /> }}
            />
          </Card>
        </TabPane>
      </Tabs>

      {/* 充值弹窗 */}
      <Modal
        title={<span><WalletOutlined style={{ marginRight: 8 }} />充值余额</span>}
        open={rechargeVisible}
        onCancel={() => { setRechargeVisible(false); setCustomAmount(null); }}
        footer={null}
        width={520}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>选择充值套餐</Text>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 10 }}>
            {RECHARGE_PACKAGES.map(pkg => (
              <div
                key={pkg.amount}
                onClick={() => { setSelectedPackage(pkg.amount); setCustomAmount(null); }}
                style={{
                  border: `2px solid ${(customAmount ? customAmount === pkg.amount : selectedPackage === pkg.amount) ? '#1890ff' : '#d9d9d9'}`,
                  borderRadius: 8,
                  padding: '12px 8px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                  background: (customAmount ? false : selectedPackage === pkg.amount) ? '#e6f7ff' : '#fff',
                  transition: 'all 0.2s'
                }}
              >
                {pkg.tag && (
                  <div style={{
                    position: 'absolute', top: -1, right: -1,
                    background: '#ff4d4f', color: '#fff',
                    fontSize: 10, padding: '1px 5px', borderRadius: '0 6px 0 6px'
                  }}>{pkg.tag}</div>
                )}
                <div style={{ fontSize: 20, fontWeight: 700, color: '#1890ff' }}>{pkg.label}</div>
                {pkg.bonus && <div style={{ fontSize: 11, color: '#52c41a', marginTop: 2 }}>{pkg.bonus}</div>}
              </div>
            ))}
          </div>
        </div>

        <Divider plain>或自定义金额</Divider>
        <Space style={{ marginBottom: 16 }}>
          <InputNumber
            placeholder="自定义金额（元）"
            min={1} max={10000} precision={0}
            value={customAmount}
            onChange={v => { setCustomAmount(v); setSelectedPackage(null); }}
            style={{ width: 180 }}
            prefix="¥"
          />
          <Text type="secondary">1-10000 元</Text>
        </Space>

        <div style={{ marginBottom: 20 }}>
          <Text strong>支付方式</Text>
          <div style={{ marginTop: 8 }}>
            <Radio.Group value={payMethod} onChange={e => setPayMethod(e.target.value)}>
              <Radio.Button value="wechat">💚 微信支付</Radio.Button>
              <Radio.Button value="alipay">💙 支付宝</Radio.Button>
              <Radio.Button value="balance">🏦 对公转账</Radio.Button>
            </Radio.Group>
          </div>
        </div>

        {finalAmount > 0 && (
          <Card size="small" style={{ background: '#f0f5ff', border: '1px solid #adc6ff', marginBottom: 16 }}>
            <Space split={<Divider type="vertical" />}>
              <Text>充值：<Text strong style={{ color: '#1890ff' }}>¥{finalAmount}</Text></Text>
              {bonusInfo?.bonus && <Text style={{ color: '#52c41a' }}>赠送：{bonusInfo.bonus}</Text>}
              <Text type="secondary">支付方式：{payMethod === 'wechat' ? '微信支付' : payMethod === 'alipay' ? '支付宝' : '对公转账'}</Text>
            </Space>
          </Card>
        )}

        <Button
          type="primary"
          block
          size="large"
          icon={<ThunderboltOutlined />}
          loading={recharging}
          onClick={handleRecharge}
          disabled={!finalAmount || finalAmount <= 0}
        >
          确认充值 {finalAmount ? `¥${finalAmount}` : ''}
        </Button>
        <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginTop: 8, fontSize: 12 }}>
          充值金额实时到账，可随时查看消费明细
        </Text>
      </Modal>
    </div>
  );
}
